import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';

/**
 * Reads the `permissions` claim of the access token (the same claim the services check with
 * @RequiresPermission). The UI only hides what the backend would refuse anyway.
 */
@Injectable({ providedIn: 'root' })
export class AgentPermissionsService {
  private cachedToken: string | null = null;
  private cachedPermissions = new Set<string>();

  permissions(): Set<string> {
    const token = readToken();
    if (token !== this.cachedToken) {
      this.cachedToken = token;
      this.cachedPermissions = new Set(decodePermissions(token));
    }
    return this.cachedPermissions;
  }

  /** SUPER_ADMIN passes every @RequiresPermission check on the backend, so the UI mirrors that. */
  isSuperAdmin(): boolean {
    return decodeClaim(readToken(), 'roleName') === 'SUPER_ADMIN';
  }

  has(permission: string): boolean {
    return this.isSuperAdmin() || this.permissions().has(permission);
  }

  hasAny(...permissions: string[]): boolean {
    if (this.isSuperAdmin()) return true;
    const set = this.permissions();
    return permissions.some((p) => set.has(p));
  }
}

/** Route guard: data.permission (string or string[]: any of them) must be in the token. */
@Injectable({ providedIn: 'root' })
export class AgentPermissionGuard implements CanActivate {
  constructor(private perms: AgentPermissionsService, private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot): boolean | UrlTree {
    if (!readToken()) {
      return this.router.parseUrl('/login');
    }
    const needed = route.data?.['permission'] as string | string[] | undefined;
    if (!needed) {
      return true;
    }
    const list = Array.isArray(needed) ? needed : [needed];
    return this.perms.hasAny(...list) ? true : this.router.parseUrl('/');
  }
}

function readToken(): string | null {
  try {
    return localStorage.getItem('accessToken');
  } catch {
    return null;
  }
}

/** Decodes the JWT payload (no verification: the server verifies) and returns permissions[]. */
export function decodePermissions(token: string | null): string[] {
  const perms = decodeClaim(token, 'permissions');
  return Array.isArray(perms) ? perms.filter((p: unknown) => typeof p === 'string') : [];
}

/** Reads one claim of the JWT payload, or undefined. */
export function decodeClaim(token: string | null, claim: string): unknown {
  if (!token) return undefined;
  const parts = token.split('.');
  if (parts.length < 2) return undefined;
  try {
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    const payload = JSON.parse(new TextDecoder('utf-8').decode(bytes));
    return payload?.[claim];
  } catch {
    return undefined;
  }
}
